import { apiClient } from '@/lib/axios'
import type { LoginRequest } from '@/types/api'

/**
 * Login a user and establish a session via cookies
 * If 2FA is enabled, token_2fa parameter must be provided
 */
export const login = async (credentials: LoginRequest) => {
  return await apiClient.post('/login', credentials)
}

/**
 * Logout the current user and invalidate the session
 */
export const logout = async () => {
  return await apiClient.get('/logout')
}

/**
 * Request a password reset link for the given email
 */
export const resetPassword = async (email: string) => {
  return await apiClient.post('/resetPassword', { email })
}

/**
 * Set a new password using a reset token
 */
export const changePassword = async (params: {
  user_id: number
  token: string
  password: string
}) => {
  return await apiClient.post('/updatePassword', params)
}

/**
 * Send an email confirmation code
 */
export const confirmEmail = async (email: string) => {
  return await apiClient.post('/confirmEmail', { email })
}

/**
 * Register a new user
 */
export const register = async (params: {
  user_name: string
  email: string
  password: string
  confirmation_code: number
}) => {
  return await apiClient.post('/addUser', params)
}

/**
 * Generate 2FA QR code and secret
 */
export const generate2fa = async () => {
  return await apiClient.get('/generate2fa')
}

/**
 * Enable 2FA for the current user
 */
export const enable2fa = async (params: {
  password: string
  token_email: number
  token_2fa: string
}) => {
  return await apiClient.post('/enable2fa', params)
}

/**
 * Disable 2FA for the current user
 */
export const disable2fa = async (token: string) => {
  return await apiClient.post('/disable2fa', { token })
}

/**
 * Verify 2FA token during login process
 * Used when logging in with 2FA enabled
 */
export const verify2fa = async (params: {
  username: string
  password: string
  token_2fa: string
  captcha_token?: string
}) => {
  const requestBody: any = {
    username: params.username,
    password: params.password, // Password is required for 2FA verification
    token_2fa: params.token_2fa
  }

  // Include captcha_token if provided
  if (params.captcha_token) {
    requestBody.captcha_token = params.captcha_token
  }

  return await apiClient.post('/login', requestBody)
}

/**
 * Update password for logged in user
 */
export const updateLoggedPassword = async (params: {
  old_password: string
  new_password: string
}) => {
  return await apiClient.post('/updateLoggedPassword', params)
}
